import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface GoalAllocation {
  goal_id: string;
  goal_name: string;
  goal_preset_id: string;
  allocated: number; // net amount allocated from this account to this goal
}

export function useAccountGoalAllocations(accountId?: string) {
  const { user } = useAuth();
  const [allocations, setAllocations] = useState<GoalAllocation[]>([]);
  const [totalAllocated, setTotalAllocated] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchAllocations = useCallback(async () => {
    if (!user || !accountId) return;
    setLoading(true);
    try {
      // Fetch all deposits for this account
      const { data: deposits, error } = await supabase
        .from('goal_deposits')
        .select('goal_id, type, amount')
        .eq('account_id', accountId);

      if (error) throw error;

      // Get goals from user metadata via supabase auth
      const {
        data: { user: supaUser },
      } = await supabase.auth.getUser();
      const goals =
        (supaUser?.user_metadata?.goals as Array<{
          id: string;
          name: string;
          presetId: string;
          status: string;
        }>) || [];

      // Group by goal_id and compute net allocation
      const byGoal = new Map<string, number>();
      for (const d of deposits || []) {
        const current = byGoal.get(d.goal_id) || 0;
        const delta = d.type === 'deposit' ? Number(d.amount) : -Number(d.amount);
        byGoal.set(d.goal_id, current + delta);
      }

      const result: GoalAllocation[] = [];
      let total = 0;
      for (const [goalId, amount] of byGoal) {
        if (amount <= 0) continue;
        const goal = goals.find((g) => g.id === goalId);
        result.push({
          goal_id: goalId,
          goal_name: goal?.name || 'Objectif',
          goal_preset_id: goal?.presetId || 'custom',
          allocated: amount,
        });
        total += amount;
      }

      setAllocations(result);
      setTotalAllocated(total);
    } catch (err) {
      console.error('Error fetching account goal allocations:', err);
    } finally {
      setLoading(false);
    }
  }, [user, accountId]);

  useEffect(() => {
    fetchAllocations();
  }, [fetchAllocations]);

  return { allocations, totalAllocated, loading, refetch: fetchAllocations };
}
